import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import DataSourceIndicator from './DataSourceIndicator';

const WidgetContainer = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem;
  box-shadow: ${theme.shadows.sm};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    box-shadow: ${theme.shadows.md};
    transform: translateY(-2px);
  }
  
  ${props => props.isDragging && `
    transform: rotate(5deg);
    box-shadow: ${theme.shadows.lg};
    z-index: 1000;
  `}
`;

const WidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
`;

const WidgetTitle = styled.h3`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const WidgetIcon = styled.span`
  font-size: ${theme.fontSizes.lg};
`;

const WidgetStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.medium};
  color: ${props => {
    switch (props.status) {
      case 'critical': return theme.colors.status.critical;
      case 'warning': return theme.colors.status.warning;
      case 'offline': return theme.colors.status.offline;
      default: return theme.colors.status.healthy;
    }
  }};
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.status) {
      case 'critical': return theme.colors.status.critical;
      case 'warning': return theme.colors.status.warning;
      case 'offline': return theme.colors.status.offline;
      default: return theme.colors.status.healthy;
    }
  }};
  animation: ${props => props.status === 'offline' ? 'none' : 'pulse 2s infinite'};
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const WidgetContent = styled.div`
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const WidgetValue = styled.div`
  font-size: ${props => props.size === 'large' ? theme.fontSizes['4xl'] : theme.fontSizes['2xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.25rem;
  text-align: center;
`;

const WidgetUnit = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  margin-left: 0.25rem;
`;

const WidgetTimestamp = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  text-align: center;
  margin-top: 0.5rem;
`;

const TrendIndicator = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: ${theme.fontSizes.sm};
  color: ${props => {
    switch (props.trend) {
      case 'up': return theme.colors.status.healthy;
      case 'down': return theme.colors.status.warning;
      default: return theme.colors.text.secondary;
    }
  }};
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${theme.colors.border.secondary};
  border-top: 3px solid ${theme.colors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.status.critical};
  font-size: ${theme.fontSizes.sm};
  text-align: center;
  padding: 1rem;
`;

const BaseWidget = ({ 
  widget, 
  data, 
  loading, 
  error, 
  onEdit, 
  onDelete,
  isDragging = false,
  size = 'medium',
  isLive = false,
  lastUpdate = null
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatValue = (value, unit) => {
    if (value === null || value === undefined) return '--';
    
    if (typeof value === 'number') {
      return value.toFixed(widget.config?.decimalPlaces || 1);
    }
    
    return value.toString();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '--';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) { // Less than 1 minute
      return 'Just now';
    } else if (diff < 3600000) { // Less than 1 hour
      return `${Math.floor(diff / 60000)}m ago`;
    } else if (diff < 86400000) { // Less than 1 day
      return `${Math.floor(diff / 3600000)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'critical': return 'Critical';
      case 'warning': return 'Warning';
      case 'offline': return 'Offline';
      default: return 'Normal';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return '↗';
      case 'down': return '↘';
      default: return '→';
    }
  };

  return (
    <WidgetContainer
      isDragging={isDragging}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <DataSourceIndicator isLive={isLive} isDemo={widget?.config?.dataMode === 'demo'} lastUpdate={lastUpdate} />
      <WidgetHeader>
        <WidgetTitle>
          <WidgetIcon>{widget.config?.icon || widget.icon || '📊'}</WidgetIcon>
          {widget.widget_name || widget.name || 'Widget'}
        </WidgetTitle>
        <WidgetStatus status={data?.status || 'offline'}>
          <StatusDot status={data?.status || 'offline'} />
          {getStatusText(data?.status || 'offline')}
        </WidgetStatus>
      </WidgetHeader>

      <WidgetContent>
        {loading && <LoadingSpinner />}
        
        {error && (
          <ErrorMessage>
            {error}
          </ErrorMessage>
        )}
        
        {!loading && !error && data && (
          <>
            <WidgetValue size={size}>
              {formatValue(data.value, data.unit)}
              <WidgetUnit>{data.unit}</WidgetUnit>
            </WidgetValue>
            
            {data.trend && (
              <TrendIndicator trend={data.trend}>
                {getTrendIcon(data.trend)}
              </TrendIndicator>
            )}
            
            <WidgetTimestamp>
              {formatTimestamp(data.timestamp)}
            </WidgetTimestamp>
          </>
        )}
        
        {!loading && !error && !data && (
          <div style={{ textAlign: 'center', color: theme.colors.text.secondary }}>
            No data available
          </div>
        )}
      </WidgetContent>

      {isHovered && (
        <div style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          display: 'flex',
          gap: '0.25rem'
        }}>
          {onEdit && (
            <button
              onClick={() => onEdit(widget)}
              style={{
                background: theme.colors.primary.main,
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                fontSize: theme.fontSizes.xs,
                cursor: 'pointer'
              }}
            >
              Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(widget)}
              style={{
                background: theme.colors.status.critical,
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                fontSize: theme.fontSizes.xs,
                cursor: 'pointer'
              }}
            >
              Delete
            </button>
          )}
        </div>
      )}
    </WidgetContainer>
  );
};

export default BaseWidget;